import type { PublicOrganizationQuery } from '@alice-financial/alice-sdk'
import { List, ListItemLink, ListItemText, Paper } from '@alice-financial/pretext-ui'
import * as React from 'react'

type Employer = NonNullable<PublicOrganizationQuery['publicOrganization']>['enrollmentEmployers'][number]

type EmployerWithUniqueIdentifier = Employer & { uniqueIdentifier: string }

type EmployerSelectionListProps = {
  employers: Array<Employer>
}

/**
 * Generates a display name for an employer, optionally including the address
 * if there are duplicate names
 */
const getUniqueIdentifier = (employer: Employer, hasDuplicateName: boolean): string => {
  const baseName = employer.marketingFriendlyName || `Employer ${employer.id}`

  if (hasDuplicateName && employer.address?.line1) {
    return `${baseName} (${employer.address.line1})`
  }

  return baseName
}

/**
 * Filters employers to only include those with unique display names.
 * When multiple employers have the same display name (even after including address),
 * only the one with the highest enrolledCount is kept.
 */
const getUniqueEmployersWithDisplayNames = (
  employers: Array<Employer>
): Array<EmployerWithUniqueIdentifier> => {
  // First pass: determine which base names appear more than once
  const baseNameCounts = employers.reduce(
    (acc, employer) => {
      const baseName = employer.marketingFriendlyName || employer.name || `Employer ${employer.id}`
      acc[baseName] = (acc[baseName] ?? 0) + 1
      return acc
    },
    {} as Record<string, number>
  )

  // Second pass: create employers with display names
  const employersWithUniqueIdentifier: Array<EmployerWithUniqueIdentifier> = employers.map((employer) => {
    const baseName = employer.marketingFriendlyName || employer.name || `Employer ${employer.id}`
    const hasDuplicateName = (baseNameCounts[baseName] ?? 0) > 1
    const uniqueIdentifier = getUniqueIdentifier(employer, hasDuplicateName)

    return { ...employer, uniqueIdentifier }
  })

  // Third pass: deduplicate by display name, keeping the one with highest enrolledCount
  const uniqueIdentifierMap = new Map<string, EmployerWithUniqueIdentifier>()

  for (const employer of employersWithUniqueIdentifier) {
    const existing = uniqueIdentifierMap.get(employer.uniqueIdentifier)

    if (!existing || employer.reachableCount > existing.reachableCount) {
      uniqueIdentifierMap.set(employer.uniqueIdentifier, employer)
    }
  }

  return Array.from(uniqueIdentifierMap.values()).sort((a, b) =>
    a.uniqueIdentifier.localeCompare(b.uniqueIdentifier)
  )
}

export const EmployerSelectionList = ({ employers }: EmployerSelectionListProps) => {
  const uniqueEmployers = getUniqueEmployersWithDisplayNames(employers)

  return (
    <Paper>
      <List disablePadding>
        {uniqueEmployers.map((employer, index) => (
          <ListItemLink
            key={employer.id}
            to={`/enroll/${employer.inviteCode}?slide=2`}
            sx={{ borderBottom: index < uniqueEmployers.length - 1 ? 1 : 0, borderColor: 'divider' }}
          >
            <ListItemText
              primary={employer.marketingFriendlyName || employer.name}
              secondary={employer.address?.line1}
            />
          </ListItemLink>
        ))}
      </List>
    </Paper>
  )
}
